/**
 * AudioSystem — sons do jogo dirigidos por DIFF do estado da simulação.
 *
 * O core não emite eventos (§5.4: sistemas leem estado); o áudio observa as
 * mesmas transições que o render já observa: bala nova = tiro, entidade
 * morta = explosão, seção nova = explosão grande, jato em aviso = alerta.
 * Nenhum som toca dentro da simulação — áudio é 100% apresentação.
 *
 * PACOTE APROVADO (playtest 4): motor = `engine_loop_e` (turbina), tiro =
 * `shoot_a`. Os arquivos são assets versionados em `public/assets/audio/`,
 * masterizados e com proveniência em `assets-src/audio/provenance/` — não
 * são mais candidatos servidos de fora do git. Os descartados seguem em
 * `assets-src/audio/quarantine/` caso a escolha precise voltar atrás.
 */

import type Phaser from 'phaser'
import { onKey } from '../input/keys'
import { ENTITY, FUEL, SCROLL, type Sim } from '@pixelraid/core'

/** Intervalo entre bips da sirene de combustível (§9: bip intermitente). */
const LOW_FUEL_INTERVAL_MS = 650

/** Sons escolhidos no gate. Trocar aqui é trocar o som do jogo. */
const SFX = {
  engine: 'engine_loop_e',
  shoot: 'shoot_a',
  explosionSmall: 'explosion_small_a',
  explosionBig: 'explosion_big_a',
  lowFuel: 'low_fuel_a',
  jetWarning: 'jet_warning_a',
  refuelLoop: 'refuel_loop_a',
  refuelFull: 'refuel_full_a',
} as const

/**
 * MIXAGEM — todos os níveis num lugar só, como o config.ts faz com o game
 * feel. Espalhar volume pelo arquivo é o caminho para um som brigar com outro
 * sem ninguém achar onde.
 *
 * `shoot` desceu de 0,50 para 0,25 (−6 dB) no playtest 4: não era o número
 * mais alto da lista, mas é de LONGE o mais repetido — em cadência de tiro
 * ele soma sobre si mesmo e domina a mistura. Volume de evento raro (explosão,
 * alerta) pode ser alto; o de evento contínuo, não.
 */
const MIX = {
  master: 0.5,
  engine: 0.35,
  shoot: 0.25,
  explosionSmall: 0.7,
  explosionBig: 0.9,
  planeDeath: 0.8,
  jetWarning: 0.8,
  refuelLoop: 0.5,
  refuelFull: 0.7,
  lowFuel: 0.6,
} as const

/**
 * Volume do jogador, em passos inteiros de 0 a `VOLUME_STEPS`.
 *
 * Passos, e não um float: o menu é navegado por tecla, e valor contínuo ali
 * vira ou uma barra que ninguém acerta ou uma casa decimal que ninguém quer.
 * Fica guardado em `pixelraid:volume` — o prefixo `pixelraid:` já é
 * sincronizado pelo save na nuvem, então o ajuste acompanha o jogador entre
 * aparelhos sem precisar mexer no registro da VM.
 *
 * É INDEPENDENTE do mute: mute é o corte rápido (tecla M no jogo), volume é o
 * nível. Zerar o volume silencia igual, mas quem só quer abaixar não perde a
 * referência do próprio nível ao usar o M.
 */
const VOLUME_KEY = 'pixelraid:volume'
export const VOLUME_STEPS = 5

export function loadVolume(): number {
  const cru = localStorage.getItem(VOLUME_KEY)
  // A chave ausente tem de ser testada ANTES da conversão: `Number(null)` é
  // 0, não NaN, então uma guarda só de `isFinite` deixa passar — e o jogador
  // novo abria o jogo com o volume em 0%, mudo, sem ter escolhido nada.
  // Vazio também vira 0 pelo mesmo caminho.
  if (cru === null || cru.trim() === '') return VOLUME_STEPS
  const bruto = Number(cru)
  if (!Number.isFinite(bruto) || bruto < 0 || bruto > VOLUME_STEPS) return VOLUME_STEPS
  return Math.round(bruto)
}

export function saveVolume(passos: number): void {
  const v = Math.min(VOLUME_STEPS, Math.max(0, Math.round(passos)))
  localStorage.setItem(VOLUME_KEY, String(v))
}

export class AudioSystem {
  private readonly scene: Phaser.Scene
  private engine: Phaser.Sound.BaseSound | undefined
  private muted = false
  /** Fração do mestre escolhida pelo jogador (0..1). */
  private volume = loadVolume() / VOLUME_STEPS

  private prevBulletsAlive = 0
  private prevEntityAlive = new Map<string, boolean>()
  private prevSections = 0
  private prevPlaneState = 'alive'
  private warnedJets = new Set<string>()
  private lastLowFuelAt = 0
  private refuelSound: Phaser.Sound.BaseSound | undefined
  private wasFull = true

  constructor(scene: Phaser.Scene) {
    this.scene = scene
    this.muted = localStorage.getItem('pixelraid:muted') === '1'
  }

  /** Nível mestre efetivo: mixagem do config vezes a escolha do jogador. */
  private get master(): number {
    return MIX.master * this.volume
  }

  /** Chaves de áudio a carregar no preload da cena. */
  static preload(loader: Phaser.Loader.LoaderPlugin): void {
    for (const key of Object.values(SFX)) {
      loader.audio(key, `assets/audio/${key}.mp3`)
    }
  }

  /**
   * Liga o mute. Chamar no create(). As teclas 1-6 de troca de candidato
   * saíram com o fim do gate de seleção.
   * Por EVENTO ('keydown-X'): Key.on('down') não dispara no Phaser 4 — o
   * caminho de evento é o mesmo do menu/lobby, provado no e2e.
   */
  bindKeys(keyboard: Phaser.Input.Keyboard.KeyboardPlugin): void {
    onKey(keyboard, 'keydown-M', () => {
      this.muted = !this.muted
      localStorage.setItem('pixelraid:muted', this.muted ? '1' : '0')
      if (this.muted) this.engine?.stop()
    })
  }

  /** Para o que é global (o motor vive no SoundManager, não na cena). */
  destroy(): void {
    this.engine?.stop()
    this.engine = undefined
    this.refuelSound?.stop()
    this.refuelSound = undefined
  }

  get status(): string {
    return this.muted ? 'MUDO' : 'som ok'
  }

  private play(key: string, volume = 1): void {
    if (this.muted || this.volume === 0) return
    // Os assets agora são versionados, então a chave deve existir sempre. A
    // guarda fica porque som que falta não pode derrubar partida: falha de
    // rede no carregamento vira silêncio, não exceção no meio do jogo.
    if (!this.scene.cache.audio.exists(key)) return
    // O unlock do AudioContext acontece no 1º gesto (política do navegador);
    // antes disso o play falha silenciosamente — aceitável.
    this.scene.sound.play(key, { volume: volume * this.master })
  }

  /** Observa o estado do tick corrente e dispara os sons das transições. */
  update(sim: Sim): void {
    // ── Motor: loop contínuo com pitch acompanhando o scroll (§7.1). ──
    if (!this.muted && this.volume > 0 && sim.planes[0]?.state === 'alive') {
      if ((this.engine === undefined || !this.engine.isPlaying) && this.scene.cache.audio.exists(SFX.engine)) {
        this.engine = this.scene.sound.add(SFX.engine, {
          loop: true,
          volume: MIX.engine * this.master,
        })
        this.engine.play()
      }
      // 0,8× no freio → 1,4× no boost, linear pela velocidade.
      if (this.engine !== undefined) {
        const ratio = (sim.scrollSpeed - SCROLL.BRAKE_FP) / (SCROLL.BOOST_FP - SCROLL.BRAKE_FP)
        ;(this.engine as Phaser.Sound.WebAudioSound).setRate(0.8 + 0.6 * Math.max(0, Math.min(1, ratio)))
      }
    } else {
      this.engine?.stop()
    }

    // ── Tiro: contagem de balas vivas subiu ⇒ disparo novo. ──
    let bulletsAlive = 0
    for (const bullet of sim.bullets) if (bullet.alive) bulletsAlive++
    if (bulletsAlive > this.prevBulletsAlive) this.play(SFX.shoot, MIX.shoot)
    this.prevBulletsAlive = bulletsAlive

    // ── Explosões: entidade viva→morta. Ponte = explosão grande (§8). ──
    const seen = new Map<string, boolean>()
    for (const entity of sim.entities) {
      const key = `${entity.chunk}:${entity.slot}`
      seen.set(key, entity.alive)
      if (!entity.alive && this.prevEntityAlive.get(key) === true && entity.type !== 'bridge') {
        // Jato que saiu da tela não explode — só o abatido (dentro dela).
        if (entity.type === 'jet' && (entity.x < 0 || entity.x > 512 * 256)) continue
        this.play(SFX.explosionSmall, MIX.explosionSmall)
      }

      // ── Aviso de jato: 300 ms antes de cruzar (§8). ──
      if (entity.type === 'jet' && entity.awake && entity.waitTicks === ENTITY.JET.WARNING_TICKS - 1) {
        if (!this.warnedJets.has(key)) {
          this.warnedJets.add(key)
          this.play(SFX.jetWarning, MIX.jetWarning)
        }
      }
    }
    this.prevEntityAlive = seen

    if (sim.sectionsCleared > this.prevSections) this.play(SFX.explosionBig, MIX.explosionBig)
    this.prevSections = sim.sectionsCleared

    // ── Morte do avião. ──
    const planeState = sim.planes[0]?.state ?? 'gone'
    if (planeState === 'exploding' && this.prevPlaneState === 'alive') {
      this.play(SFX.explosionBig, MIX.planeDeath)
    }
    this.prevPlaneState = planeState

    // ── Reabastecimento (§9): bomba em loop com pitch subindo com o tanque;
    //    ding na hora em que enche (§17.2 do pipeline: refuel_loop/full). ──
    const refuelingNow = sim.planes[0]?.refueling === true
    if (refuelingNow && !this.muted && this.volume > 0) {
      if (
        (this.refuelSound === undefined || !this.refuelSound.isPlaying) &&
        this.scene.cache.audio.exists(SFX.refuelLoop)
      ) {
        this.refuelSound = this.scene.sound.add(SFX.refuelLoop, {
          loop: true,
          volume: MIX.refuelLoop * this.master,
        })
        this.refuelSound.play()
      }
      if (this.refuelSound !== undefined) {
        const fill = Math.max(0, Math.min(1, (sim.planes[0]?.fuel ?? 0) / FUEL.TANK))
        ;(this.refuelSound as Phaser.Sound.WebAudioSound).setRate(0.9 + 0.5 * fill)
      }
    } else {
      this.refuelSound?.stop()
    }
    // Ding de tanque cheio: transição para cheio DURANTE reabastecimento.
    const fullNow = (sim.planes[0]?.fuel ?? 0) >= FUEL.TANK
    if (fullNow && !this.wasFull && refuelingNow) this.play(SFX.refuelFull, MIX.refuelFull)
    this.wasFull = fullNow

    // ── Sirene de combustível (§9): bip intermitente enquanto fuel < 30. ──
    const plane = sim.planes[0]
    if (plane !== undefined && plane.state === 'alive' && plane.fuel < FUEL.LOW_WARNING) {
      const now = this.scene.time.now
      if (now - this.lastLowFuelAt > LOW_FUEL_INTERVAL_MS) {
        this.lastLowFuelAt = now
        this.play(SFX.lowFuel, MIX.lowFuel)
      }
    }
  }
}
